import { FC, useEffect, useMemo, useState } from 'react';

import { router } from '@inertiajs/react';

import {
    AddedOrderEditDraft,
    BuyerOrderEditItems,
    ExistingOrderEditDraft,
} from './BuyerOrderEditItems';
import { OrderOfferSearch } from './OrderOfferSearch';
import { deliveryMethodLabel, formatOrderAmount } from './orderRevisionUtils';
import { OrderServiceFeePreview } from './OrderServiceFeePreview';
import { Button } from '../../../components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/Dialog';
import { profileOrderBuyerUpdateRoute } from '../../../models/routes';
import { Order, OrderDelivery } from '../../../types';

interface Props {
    order: Order | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const BuyerOrderEditDialog: FC<Props> = ({
    order,
    open,
    onOpenChange,
}) => {
    const [items, setItems] = useState<ExistingOrderEditDraft[]>([]);
    const [addedItems, setAddedItems] = useState<AddedOrderEditDraft[]>([]);
    const [deliveryMethod, setDeliveryMethod] = useState<
        OrderDelivery['method'] | ''
    >('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !order) return;

        setItems(
            (order.order_items || []).map((item) => ({
                order_item_id: item.id,
                offer_id: item.offer?.id || 0,
                quantity: item.amount,
                removed: false,
            })),
        );
        setAddedItems([]);
        setDeliveryMethod(
            order.revision_delivery_method || order.delivery?.method || '',
        );
        setError('');
    }, [open, order]);

    const orderItemsById = useMemo(
        () =>
            new Map((order?.order_items || []).map((item) => [item.id, item])),
        [order],
    );
    const currentDeliveryMethod = order?.delivery?.method || '';
    const existingChanged = items.some((draft) => {
        const original = orderItemsById.get(draft.order_item_id);
        return draft.removed || draft.quantity !== original?.amount;
    });
    const changed =
        existingChanged ||
        addedItems.length > 0 ||
        deliveryMethod !== currentDeliveryMethod;
    const activeItemsCount =
        items.filter((item) => !item.removed).length + addedItems.length;
    const invalidQuantity =
        items.some((draft) => {
            if (draft.removed) return false;
            const item = orderItemsById.get(draft.order_item_id);
            return (
                draft.quantity < 1 ||
                draft.quantity >
                    (item?.maximum_revision_quantity ?? item?.amount ?? 1)
            );
        }) ||
        addedItems.some(
            ({ offer, quantity }) => quantity < 1 || quantity > offer.amount,
        );
    const newTotal = useMemo(() => {
        if (!order) return 0;

        const existingTotal = items.reduce((total, draft) => {
            const item = orderItemsById.get(draft.order_item_id);
            if (!item || draft.removed) return total;
            return total + item.price_per_item * draft.quantity;
        }, 0);
        return addedItems.reduce(
            (total, draft) => total + draft.offer.price * draft.quantity,
            existingTotal,
        );
    }, [addedItems, items, order, orderItemsById]);

    if (!order) return null;

    const submit = () => {
        if (!changed || invalidQuantity || activeItemsCount === 0) return;

        setProcessing(true);
        setError('');
        router.patch(
            profileOrderBuyerUpdateRoute(order.id),
            {
                order: {
                    delivery_method: deliveryMethod,
                    items: [
                        ...items,
                        ...addedItems.map(({ offer, quantity }) => ({
                            offer_id: offer.id,
                            quantity,
                            removed: false,
                        })),
                    ],
                },
            },
            {
                preserveScroll: true,
                onError: (errors) =>
                    setError(
                        String(
                            errors.general || 'Не вдалося оновити замовлення.',
                        ),
                    ),
                onSuccess: () => onOpenChange(false),
                onFinish: () => setProcessing(false),
            },
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="h-[100dvh] w-full max-w-none gap-0 overflow-hidden rounded-none p-0 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100%-2rem)] sm:max-w-3xl sm:rounded-2xl">
                <DialogHeader className="border-b px-4 py-5 pr-12 text-left sm:px-6">
                    <DialogTitle>
                        Редагування замовлення #{order.number}
                    </DialogTitle>
                    <DialogDescription>
                        Зміни застосуються одразу. Редагування доступне до
                        підтвердження продавцем.
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
                    <BuyerOrderEditItems
                        items={items}
                        addedItems={addedItems}
                        orderItemsById={orderItemsById}
                        onToggleRemoved={(orderItemId) =>
                            setItems((current) =>
                                current.map((entry) =>
                                    entry.order_item_id === orderItemId
                                        ? {
                                              ...entry,
                                              removed: !entry.removed,
                                          }
                                        : entry,
                                ),
                            )
                        }
                        onExistingQuantityChange={(orderItemId, quantity) =>
                            setItems((current) =>
                                current.map((entry) =>
                                    entry.order_item_id === orderItemId
                                        ? { ...entry, quantity }
                                        : entry,
                                ),
                            )
                        }
                        onRemoveAdded={(offerId) =>
                            setAddedItems((current) =>
                                current.filter(
                                    (entry) => entry.offer.id !== offerId,
                                ),
                            )
                        }
                        onAddedQuantityChange={(offerId, quantity) =>
                            setAddedItems((current) =>
                                current.map((entry) =>
                                    entry.offer.id === offerId
                                        ? { ...entry, quantity }
                                        : entry,
                                ),
                            )
                        }
                    />

                    <OrderOfferSearch
                        orderId={order.id}
                        excludedOfferIds={[
                            ...items.map((item) => item.offer_id),
                            ...addedItems.map((item) => item.offer.id),
                        ]}
                        onAdd={(offer) =>
                            setAddedItems((current) => [
                                ...current,
                                { offer, quantity: 1 },
                            ])
                        }
                    />

                    <section>
                        <h3 className="mb-3 text-sm font-semibold">
                            Спосіб доставки
                        </h3>
                        <div className="grid gap-2 sm:grid-cols-3">
                            {(
                                order.available_revision_delivery_methods || []
                            ).map((method) => (
                                <label
                                    key={method}
                                    className={`cursor-pointer rounded-xl border p-3 text-sm ${deliveryMethod === method ? 'border-primary bg-primary/5 font-semibold' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="buyer_order_delivery_method"
                                        className="mr-2"
                                        checked={deliveryMethod === method}
                                        onChange={() =>
                                            setDeliveryMethod(method)
                                        }
                                    />
                                    {deliveryMethodLabel(method)}
                                </label>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="border-t bg-background px-4 py-4 sm:px-6">
                    {activeItemsCount === 0 && (
                        <p className="mb-2 text-sm text-destructive">
                            Не можна видалити всі товари із замовлення.
                        </p>
                    )}
                    {error && (
                        <p className="mb-2 text-sm text-destructive">{error}</p>
                    )}
                    <div className="mb-3 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                            Нова сума:
                        </span>
                        <strong>{formatOrderAmount(newTotal)}</strong>
                    </div>
                    <OrderServiceFeePreview
                        applicable={Boolean(order.service_fee_applicable)}
                        currentFeeCents={order.service_fee_cents || 0}
                        newTotal={newTotal}
                    />
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={processing}
                            onClick={() => onOpenChange(false)}
                        >
                            Скасувати
                        </Button>
                        <Button
                            type="button"
                            disabled={
                                processing ||
                                !changed ||
                                invalidQuantity ||
                                activeItemsCount === 0
                            }
                            onClick={submit}
                        >
                            {processing ? 'Збереження…' : 'Зберегти зміни'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
